import type { FC } from 'react';
import { Show, ShowFirstMatching } from '@components/Show';
import { sidebarBlockItemClasses, sidebarBlockListClasses } from '@ContractBuilder/components/shared-sidebar';
import { useNavigation } from '@ContractBuilder/modules/navigation/context';
import { SortedBlockItem } from '@ContractBuilder/modules/navigation/modules/nav-section/views/SortedBlockItem';
import { useMultiSelectStore } from '@ContractBuilder/store/multi-select.store';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ResourceBlock } from '@domain/schemas/contract.schema';
import { isBlockOrInlineAttachment } from '@features/attachments/utils/is-inline-attachment-or-block';
import { SortedBlockInlineAttachment } from '@features/attachments/views/NavInlineAttachment';
import clsx from 'clsx';

interface NavSectionBlocksProps {
  areOperationsHidden?: boolean;
  blocks: ResourceBlock[];
  excludedBlockIds?: string[];
  isDragDisabled: boolean;
  onClick: (blockId: string) => void;
  onMouseEnter: (blockId: string) => void;
  onMouseLeave: () => void;
  sectionId: string;
  shouldDisableOperations?: boolean;
}

interface SortableBlockWrapperProps {
  areOperationsHidden: boolean;
  block: ResourceBlock;
  isDragDisabled: boolean;
  isExcludedFromSummary: boolean;
  onClick: (blockId: string) => void;
  onMouseEnter: (blockId: string) => void;
  onMouseLeave: () => void;
  sectionId: string;
  shouldDisableOperations: boolean;
}

const SortableBlockWrapper: FC<SortableBlockWrapperProps> = ({
  areOperationsHidden,
  block,
  isDragDisabled,
  isExcludedFromSummary,
  onClick,
  onMouseEnter,
  onMouseLeave,
  sectionId,
  shouldDisableOperations,
}) => {
  const { attachments } = useNavigation();
  const isMultiSelectActiveInThisPart = useMultiSelectStore(
    (state) => state.activePartId === sectionId && state.selectedBlockIds.size > 0,
  );
  const effectiveIsDragDisabled = isDragDisabled || isMultiSelectActiveInThisPart;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
    disabled: effectiveIsDragDisabled,
    data: { type: 'block', block, sectionId },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const dragListeners: DraggableSyntheticListeners | undefined = listeners || undefined;
  const dragAttributes: DraggableAttributes = attributes;

  const attachment = attachments?.find((attachment) => attachment.id === block.attachment_id);
  const isAttachment = isBlockOrInlineAttachment(block) === 'attachment';

  return (
    <li
      id={`${block.id}-draggable`}
      data-testid={`${block.name}-draggable-block-parent`}
      key={block.id}
      className={sidebarBlockItemClasses()}
      onClick={onClick.bind(null, block.id)}
      onMouseEnter={onMouseEnter.bind(null, block.id)}
      onMouseLeave={onMouseLeave}
      ref={setNodeRef}
      style={style}
    >
      <ShowFirstMatching>
        <Show when={isAttachment && attachment}>
          <SortedBlockInlineAttachment
            attachment={attachment!}
            block={block}
            listeners={dragListeners}
            attributes={dragAttributes}
            isDragDisabled={effectiveIsDragDisabled}
            shouldDisableOperations={shouldDisableOperations}
          />
        </Show>
        <Show when={true}>
          <SortedBlockItem
            areOperationsHidden={areOperationsHidden}
            block={block}
            listeners={dragListeners}
            attributes={dragAttributes}
            isDragDisabled={effectiveIsDragDisabled}
            isExcludedFromSummary={isExcludedFromSummary}
            shouldDisableOperations={shouldDisableOperations}
          />
        </Show>
      </ShowFirstMatching>
    </li>
  );
};

export const NavSectionBlocks: FC<NavSectionBlocksProps> = ({
  areOperationsHidden = false,
  blocks = [],
  excludedBlockIds = [],
  isDragDisabled,
  onClick,
  onMouseEnter,
  onMouseLeave,
  sectionId,
  shouldDisableOperations = false,
}) => {
  return (
    <ol className={clsx(sidebarBlockListClasses(), 'max-w-xs truncate')}>
      {blocks.map((block) => (
        <SortableBlockWrapper
          key={block.id}
          areOperationsHidden={areOperationsHidden}
          block={block}
          isDragDisabled={isDragDisabled}
          isExcludedFromSummary={excludedBlockIds.includes(block.id)}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          sectionId={sectionId}
          shouldDisableOperations={shouldDisableOperations}
        />
      ))}
    </ol>
  );
};
